import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { abwesenheitsZuordnungQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { useAbwesenheiten } from './Abwesenheit'
import { Button, Card, CardPad } from './ui/primitives'

/**
 * Intranet-Zuordnung: welcher Intranet-Mitarbeiter ist welche Person in Work
 * (Zusatzpaket Z-01).
 *
 * Von Nils gefordert: „in den einzelnen Usern die passenden Intranet-User
 * zuordnen (ggf. Namensautoerkennung?)".
 *
 * ## Wie die Automatik gedacht ist
 *
 * Der Abgleich ordnet **von selbst** zu, wenn E-Mail oder Namensbestand
 * übereinstimmen **und** der Treffer auf beiden Seiten eindeutig ist. Alles andere
 * landet hier als Vorschlag mit einem Knopf – nach demselben Grundsatz wie beim
 * Zusammenführen von Personen ([E-22](../../../../docs/ENTSCHEIDUNGEN.md)):
 * Namensgleichheit ist ein starker Hinweis und kein Beweis.
 *
 * Warum die Automatik hier trotzdem schreiben darf, beim Zusammenführen aber
 * nicht: **eine Zuordnung ist umkehrbar.** Sie steht in dieser Liste, trägt ihren
 * Grund und lässt sich mit einem Klick lösen – und weil Work die Abwesenheiten aus
 * der Quelle nachlädt, holt ein erneutes Zuordnen sie zurück. Eine Zusammenführung
 * ist endgültig; deshalb dort nur Vorschläge.
 *
 * Seit [E-37](../../../../docs/ENTSCHEIDUNGEN.md) heißt Lösen allerdings, dass die
 * gespeicherten Zeilen beim nächsten Abgleich **entfernt** werden. Verlustfrei, aber
 * nicht folgenlos – und die Oberfläche sagt das an jeder Stelle, an der man löst.
 *
 * ## Warum das eine eigene Karte ist
 *
 * Die Benutzertabelle darüber trägt schon acht Spalten. Eine neunte für „Intranet"
 * hätte die Zuordnung zwar sichtbar gemacht, aber nicht bedienbar: dazu gehören
 * Vorschläge, Kandidatenlisten und der Abgleich-Knopf. Beides in einer Tabelle
 * wäre eng und in zwei Karten ist es lesbar.
 */
export function IntranetZuordnung() {
  const { data, isLoading, error } = useQuery(abwesenheitsZuordnungQuery)
  const abwesenheiten = useAbwesenheiten()
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  /** Frei gewählte Person je offener Intranet-Kennung. */
  const [wahl, setWahl] = useState<Record<string, string>>({})
  /**
   * Die Liste der Nicht-Zugeordneten ist **zu**, solange niemand sie aufmacht.
   * Bei 84 Intranet-Konten wäre sie sonst der längste Teil der Einstellungen.
   */
  const [offenAusgeklappt, setOffenAusgeklappt] = useState(false)
  const [suche, setSuche] = useState('')
  const queryClient = useQueryClient()

  const neuLaden = () => {
    void queryClient.invalidateQueries({ queryKey: ['absences'] })
    // Kapazität und Zuweisungs-Vorschlag tragen die Abwesenheit mit sich.
    void queryClient.invalidateQueries({ queryKey: ['people'] })
  }

  const zuordnen = useMutation({
    mutationFn: (v: { personId: string; externalId: string; name: string }) =>
      trpc.absences.zuordnen.mutate(v),
    onSuccess: (r) => {
      setMeldung({ art: 'ok', text: `„${r.name}" ist jetzt ${r.displayName}.` })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  const loesen = useMutation({
    mutationFn: (v: { externalId: string }) => trpc.absences.zuordnungLoesen.mutate(v),
    onSuccess: (r) => {
      setMeldung({
        art: 'ok',
        text: `Zuordnung für „${r.name}" gelöst. Der nächste Abgleich entfernt die gespeicherten Abwesenheiten dieses Kontos – erneutes Zuordnen holt sie zurück.`,
      })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  const abgleichen = useMutation({
    mutationFn: () => trpc.absences.abgleichAnstossen.mutate(),
    onSuccess: (r) => {
      setMeldung({
        art: 'ok',
        text:
          r.hinweis ??
          'Abgleich eingestellt. Er läuft im Hintergrund; in einigen Sekunden stehen die Zahlen hier.',
      })
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })

  // Die Abfrage ist auf `admin` beschränkt. Für alle anderen gibt es diese Karte
  // gar nicht, statt einer Fehlermeldung an einer Stelle, die sie nichts angeht.
  if (error) return null

  const offen = data?.offen ?? []
  /** Konten mit Namenstreffer – die sind der Grund, warum es diese Karte gibt. */
  const mitTreffer = offen.filter((m) => m.treffer.length > 0)

  /**
   * Eingeklappt: nur die Treffer. Ausgeklappt: alles, gefiltert.
   *
   * Der Filter vergleicht kleingeschrieben und ohne Rand-Leerzeichen – kein
   * Namensabgleich wie beim Zuordnen, sondern eine Suche. Wer „mül" eintippt, will
   * „Müller" finden und nicht über Umlaut-Lesarten nachdenken müssen; deshalb wird
   * hier bewusst **nicht** `namensgrad` benutzt.
   */
  const gefiltert = suche.trim()
    ? offen.filter((m) => m.name.toLowerCase().includes(suche.trim().toLowerCase()))
    : offen
  const sichtbarOffen = offenAusgeklappt ? gefiltert : mitTreffer

  return (
    <Card>
      <CardPad>
        <div className="flex items-baseline gap-2">
          <b className="text-[13px]">Abwesenheiten aus dem Intranet</b>
          <span className="text-xs text-muted">Urlaub und Krankheit, ohne Angabe der Art</span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            disabled={abgleichen.isPending}
            onClick={() => {
              setMeldung(null)
              abgleichen.mutate()
            }}
          >
            Jetzt abgleichen
          </Button>
        </div>

        <p className="mt-1.5 text-xs text-ink2">
          Work liest die Abwesenheiten alle 15 Minuten und zeigt sie in Leitstand, Mein Tag und beim
          Zuweisen. Die <strong>Art</strong> – Urlaub oder Krankheit – liefert die Schnittstelle
          nicht, und Work fragt sie auch nicht nach: für die Steuerung zählt „ist da / ist nicht
          da". Damit eine Abwesenheit einer Person zugeordnet werden kann, muss das Intranet-Konto
          hier verbunden sein.
        </p>

        {meldung && (
          <p
            role="status"
            className={`mt-3 rounded-lg px-2.5 py-2 text-[12.5px] ${
              meldung.art === 'ok' ? 'bg-[#eef7ee] text-[#1d6b34]' : 'bg-[#fdeaea] text-[#b02a2a]'
            }`}
          >
            {meldung.text}
          </p>
        )}

        {isLoading && <p className="mt-3 text-[13px] text-ink2">Lädt …</p>}

        {data && !data.liveGelesen && (
          /**
           * Der Rückfall ist ausdrücklich benannt, nicht stillschweigend – und seit
           * [E-37](../../../../docs/ENTSCHEIDUNGEN.md) trägt er weniger als vorher.
           *
           * Work speichert nur noch Abwesenheiten **zugeordneter** Konten. Der
           * Rückfall kann also nur Namen zeigen, die schon zugeordnet sind – für
           * eine **neue** Zuordnung hilft er nicht. Das gehört gesagt, sonst sucht
           * man in einer Liste, die den Gesuchten grundsätzlich nicht enthalten kann.
           */
          <div className="mt-3 rounded-lg bg-[#fdf3dc] px-2.5 py-2 text-[12.5px] text-[#8a6100]">
            <strong>Die Mitarbeiterliste kommt nicht aus dem Intranet.</strong> Neue Zuordnungen
            sind erst möglich, wenn das Intranet wieder antwortet – bestehende bleiben unten
            sichtbar und funktionieren weiter.
            {data.quellFehler && (
              <span className="mt-1 block text-[11.5px] opacity-90">Grund: {data.quellFehler}</span>
            )}
          </div>
        )}

        {data && (
          <p className="mt-3 text-[11px] text-muted">
            {data.zugeordnet.length} von {data.mitarbeiterGesamt} Intranet-Konten zugeordnet ·{' '}
            {abwesenheiten.liste.length} Abwesenheiten gespeichert
            {abwesenheiten.ohneZuordnung > 0 && (
              /* Kommt nur im Fenster zwischen „Zuordnung gelöst" und dem nächsten
                 Abgleich vor. Zu sagen, dass sie gleich verschwinden, ist die
                 hilfreichere Auskunft als eine Zahl ohne Erklärung. */
              <>
                {' · '}
                <span className="text-[#8a6100]">
                  {abwesenheiten.ohneZuordnung} ohne Zuordnung, werden beim nächsten Abgleich
                  entfernt
                </span>
              </>
            )}
          </p>
        )}

        <p className="mt-1.5 text-[11px] text-muted">
          Gespeichert werden <strong>nur Abwesenheiten zugeordneter Konten</strong>. Das Intranet
          liefert die ganze epcan; Work behält keine Urlaubsdaten von Menschen, die Work nicht
          benutzen. Nach einer neuen Zuordnung erscheint die Historie mit dem nächsten Abgleich –
          spätestens in 15 Minuten, oder sofort über „Jetzt abgleichen".
        </p>

        {/* ── Offene Zuordnungen ─────────────────────────────────────── */}

        {data && data.offen.length > 0 && (
          <div className="mt-3.5">
            {/**
             * **Eingeklappt, und das ist kein Verstecken.**
             *
             * Das Intranet führt die ganze epcan – rund 90 Konten. Die
             * Anwendungsentwicklung ist ein kleiner Teil davon; die übrigen brauchen
             * nie eine Zuordnung. Eine dauerhaft ausgerollte Liste mit 90 Zeilen und
             * 90 Auswahlfeldern liest sich wie eine Aufgabenliste, die niemand
             * abarbeiten soll – und sie schiebt alles Wichtige aus dem Bild.
             *
             * Was **nicht** eingeklappt wird: die Zeile mit den Zahlen darüber und
             * die Namenstreffer. Ein Treffer ist der Fall, für den es diese Karte
             * gibt; er bleibt sichtbar, auch wenn die Liste zu ist.
             */}
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-[11px] font-semibold tracking-[0.5px] text-muted uppercase">
                Ohne Zuordnung ({data.offen.length})
              </span>
              {mitTreffer.length > 0 && (
                <span className="text-[11px] font-semibold text-[#8a6100]">
                  {mitTreffer.length} mit Namenstreffer
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => setOffenAusgeklappt((o) => !o)}
                aria-expanded={offenAusgeklappt}
              >
                {offenAusgeklappt ? 'Liste verbergen' : 'Alle anzeigen'}
              </Button>
            </div>

            <p className="mt-1 text-[11px] text-muted">
              Das Intranet führt die ganze epcan. Zuordnen musst du nur, wer in Work Aufgaben
              bearbeitet – die übrigen bleiben hier stehen und stören nichts.
            </p>

            {offenAusgeklappt && (
              <input
                type="search"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="Nach Namen filtern …"
                aria-label="Intranet-Konten nach Namen filtern"
                className="mt-2.5 w-full rounded-lg border border-line2 bg-surface px-2.5 py-1.5 text-[12.5px]"
              />
            )}

            {sichtbarOffen.map((m) => {
              const gewaehlt = wahl[m.externalId] ?? m.eindeutig ?? ''
              const bester = m.treffer[0]
              return (
                <div
                  key={m.externalId}
                  className="flex flex-wrap items-center gap-2 border-b border-line py-2 text-[12.5px] last:border-b-0"
                >
                  <span className="font-semibold">{m.name}</span>

                  {bester ? (
                    <span className="text-[11px] text-muted">
                      {/* Der Grund gehört dran. „Vorschlag: Anna Berger" ohne
                          Begründung ist eine Behauptung; „gleicher Name" ist
                          eine Auskunft, die man prüfen kann. */}
                      {bester.displayName} – {bester.grundText}
                      {m.treffer.length > 1 && ` (+${m.treffer.length - 1} weitere)`}
                    </span>
                  ) : (
                    <span className="text-[11px] text-muted">kein Namenstreffer</span>
                  )}

                  <select
                    value={gewaehlt}
                    onChange={(e) => setWahl((v) => ({ ...v, [m.externalId]: e.target.value }))}
                    aria-label={`Work-Person für ${m.name}`}
                    className="ml-auto cursor-pointer rounded-lg border border-line2 bg-surface px-2 py-1 text-[12px] text-ink"
                  >
                    <option value="">Person wählen …</option>
                    {data.personen.map((p) => (
                      <option key={p.id} value={p.id} disabled={p.belegt}>
                        {p.displayName}
                        {p.belegt ? ' (schon verbunden)' : ''}
                      </option>
                    ))}
                  </select>

                  <Button
                    variant={m.eindeutig ? 'primary' : 'ghost'}
                    size="sm"
                    disabled={!gewaehlt || zuordnen.isPending}
                    onClick={() => {
                      setMeldung(null)
                      zuordnen.mutate({
                        personId: gewaehlt,
                        externalId: m.externalId,
                        name: m.name,
                      })
                    }}
                    aria-label={`${m.name} zuordnen`}
                  >
                    Zuordnen
                  </Button>
                </div>
              )
            })}

            {offenAusgeklappt && sichtbarOffen.length === 0 && (
              <p className="mt-2 text-[12.5px] text-ink2">
                Kein Intranet-Konto passt zu „{suche}".
              </p>
            )}

            {/**
             * **Kein stiller Schnitt.** Eingeklappt zeigt die Karte nur die Treffer;
             * dass darunter mehr liegt, steht dran. Eine Liste, die schweigend
             * kürzt, liest sich wie „das ist alles".
             */}
            {!offenAusgeklappt && data.offen.length > mitTreffer.length && (
              <p className="mt-2 text-[11px] text-muted">
                {mitTreffer.length === 0
                  ? `${data.offen.length} Konten ohne Namenstreffer – über „Alle anzeigen" von Hand zuordnen.`
                  : `Weitere ${data.offen.length - mitTreffer.length} ohne Namenstreffer sind eingeklappt.`}
              </p>
            )}
          </div>
        )}

        {/* ── Bestehende Zuordnungen ─────────────────────────────────── */}

        {data && data.zugeordnet.length > 0 && (
          <div className="mt-3.5">
            <div className="text-[11px] font-semibold tracking-[0.5px] text-muted uppercase">
              Zugeordnet ({data.zugeordnet.length})
            </div>
            {data.zugeordnet.map((z) => (
              <div
                key={z.zuordnungId}
                className="flex flex-wrap items-center gap-2 border-b border-line py-2 text-[12.5px] last:border-b-0"
              >
                <span className="font-semibold">{z.displayName}</span>
                <span className="text-[11px] text-muted">
                  Intranet: {z.name}
                  {/* Der Name aus der Quelle gewinnt – Heirat, Tippfehler,
                      Umbenennung. Die Kennung bleibt, der Name darf sich ändern. */}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  disabled={loesen.isPending}
                  onClick={() => {
                    setMeldung(null)
                    loesen.mutate({ externalId: z.externalId })
                  }}
                  aria-label={`Zuordnung von ${z.displayName} lösen`}
                >
                  Lösen
                </Button>
              </div>
            ))}
          </div>
        )}

        {data && data.zugeordnet.length === 0 && data.offen.length === 0 && (
          <p className="mt-3 text-[13px] text-ink2">
            Noch keine Intranet-Konten bekannt. Ist der Zugang eingerichtet (
            <code className="text-[11.5px]">INTRANET_URL</code> und{' '}
            <code className="text-[11.5px]">INTRANET_TOKEN</code> in der{' '}
            <code className="text-[11.5px]">.env</code> auf dem Server), holt der erste Abgleich sie
            – oder du stößt ihn oben an.
          </p>
        )}

        <p className="mt-3 text-[11px] text-muted">
          Eine Person trägt <strong>genau ein</strong> Intranet-Konto: zwei würden denselben Urlaub
          doppelt melden, und beim Zählen freier Kapazität fällt das niemandem auf. Eine Zuordnung
          zu lösen ist <strong>verlustfrei, aber nicht folgenlos</strong> – der nächste Abgleich
          entfernt die gespeicherten Abwesenheiten, erneutes Zuordnen holt sie zurück. Beim{' '}
          <strong>Zusammenführen von Personen</strong> zieht die Zuordnung von selbst mit.
        </p>
      </CardPad>
    </Card>
  )
}
